import React, { useEffect, useState } from "react";
import { DetailSectionComponent } from "../../../components/DetailSectionComponent";
import { HeaderProps } from "../../../components/Header";
import { useTranslation } from "react-i18next";
import {
  RouteSearchParam,
  useRouteNavigator,
} from "../../../routes/RoutesHelper";
import { useEmailTemplatesStore } from "../stores/Hooks";
import { Stack } from "@mui/material";
import { Button, useSpacing } from "@surya-digital/leo-reactjs-material-ui";
import { EmailTemplatesPageContent } from "./contents/EmailTemplatesPageContent";
import { observer } from "mobx-react";
import { Plus } from "lucide-react";
import { CreateEmailTemplateDrawer } from "../components/drawers/CreateEmailTemplateDrawer";
import { useSignInStore } from "../../store/Hooks";
import { UserPrivileges } from "../../store/user/UserPrivileges";
import { useSearchParams } from "react-router-dom";
import { EmailTemplateDetailsDrawer } from "../components/drawers/EmailTemplateDetailsDrawer";
import { Mode } from "../../enums/Mode";
import { UpdateEmailTemplateDrawer } from "../components/drawers/UpdateEmailTemplateDrawer";
import { ConfirmEmailTemplateDeletionDialog } from "../components/dialogs/ConfirmEmailTemplateDeletionDialog";
import { navigateOrGetNetworkErrorPage } from "../../../utility/navigateOrGetNetworkErrorPage";
import { useNetworkingErrorStore } from "../../networking/store/hooks";

export const EmailTemplatesPage: React.FC = observer(() => {
  const [openCreateEmailTemplateDrawer, setOpenCreateEmailTemplateDrawer] =
    useState(false);
  const [openEmailTemplateDetailsDrawer, setOpenEmailTemplateDetailsDrawer] =
    useState(false);
  const [openUpdateEmailTemplateDrawer, setOpenUpdateEmailTemplateDrawer] =
    useState(false);
  const [isFromEmailDetailsDrawer, setIsFromEmailDetailsDrawer] =
    useState(false);
  const [
    openConfirmDeleteEmailTemplateDialog,
    setOpenConfirmDeleteEmailTemplateDialog,
  ] = useState(false);
  const [searchParams] = useSearchParams();

  const { t } = useTranslation();
  const spacing = useSpacing();
  const navigateTo = useRouteNavigator();
  const emailTemplatesStore = useEmailTemplatesStore();
  const signInStore = useSignInStore();
  const errorStore = useNetworkingErrorStore();

  useEffect(() => {
    const templateId = searchParams.get(RouteSearchParam.TemplateId);
    const mode = searchParams.get(RouteSearchParam.Mode);

    if (!templateId) {
      setOpenEmailTemplateDetailsDrawer(false);
      setOpenUpdateEmailTemplateDrawer(false);
      return;
    }

    switch (mode) {
      case Mode.View:
        setOpenEmailTemplateDetailsDrawer(true);
        setOpenUpdateEmailTemplateDrawer(false);
        setIsFromEmailDetailsDrawer(true);
        break;
      case Mode.Edit:
        setOpenEmailTemplateDetailsDrawer(false);
        setOpenUpdateEmailTemplateDrawer(true);
        break;
      default:
        navigateTo.pageNotFound();
    }
  }, [searchParams]);

  useEffect(
    () => () => {
      emailTemplatesStore.resetStore();
      errorStore.removeError();
    },
    [],
  );

  const mainHeaderProps: HeaderProps = {
    title: t("emailTemplates.title"),
    prefersLargeTitle: true,
    backButtonCallback: navigateTo.project,
    actionElement: signInStore.isPrivilegeGranted(
      UserPrivileges.processGlobalEmailTemplate,
    ) ? (
      <Button
        label={t("emailTemplates.createEmailTemplate")}
        name="createEmailTemplate"
        size="medium"
        variant="filled"
        icon={<Plus />}
        onClick={() => setOpenCreateEmailTemplateDrawer(true)}
      />
    ) : undefined,
  };

  const errorPage = navigateOrGetNetworkErrorPage(
    errorStore.error,
    navigateTo,
    errorStore.removeError,
  );

  if (errorPage) {
    errorStore.removeError();
    return errorPage;
  }

  return (
    <>
      <DetailSectionComponent
        mainHeaderProps={mainHeaderProps}
        isOverflowHidden={false}
        isChildHeightVariable={false}
        isManageUsersLogoVisible={true}
        isAuditLogsLogoVisible={true}
        isEmailTemplatesLogoVisible={false}
      >
        <Stack
          justifyContent="start"
          alignItems="center"
          height="100%"
          width="100%"
          padding={spacing.spaceXL}
        >
          <EmailTemplatesPageContent
            onClickCreateEmailTemplateButton={() =>
              setOpenCreateEmailTemplateDrawer(true)
            }
            setOpenConfirmDeleteEmailTemplateDialog={
              setOpenConfirmDeleteEmailTemplateDialog
            }
          />
        </Stack>
      </DetailSectionComponent>

      <CreateEmailTemplateDrawer
        open={openCreateEmailTemplateDrawer}
        setOpen={setOpenCreateEmailTemplateDrawer}
      />
      <EmailTemplateDetailsDrawer
        open={openEmailTemplateDetailsDrawer}
        onClose={() => setIsFromEmailDetailsDrawer(false)}
        setOpenConfirmDeleteEmailTemplateDialog={
          setOpenConfirmDeleteEmailTemplateDialog
        }
      />
      <UpdateEmailTemplateDrawer
        open={openUpdateEmailTemplateDrawer}
        setOpen={setOpenUpdateEmailTemplateDrawer}
        isFromEmailDetailsDrawer={isFromEmailDetailsDrawer}
      />

      <ConfirmEmailTemplateDeletionDialog
        open={openConfirmDeleteEmailTemplateDialog}
        setOpen={setOpenConfirmDeleteEmailTemplateDialog}
      />
    </>
  );
});
